import React, { useState, useEffect } from 'react';
import { Container, Grid, Card, Text, Title, Group, Stack, Button, Badge, Image, SimpleGrid, Skeleton } from '@mantine/core';
import { IconSearch, IconMapPin, IconShare, IconArrowRight, IconHome, IconCalendar, IconCurrencyDollar, IconTent, IconPhone } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import MountainBackground from '../components/MountainBackground';

const Home = () => {
  const [newestListings, setNewestListings] = useState([]);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [featuredCampgrounds, setFeaturedCampgrounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFeaturedProperties, setLoadingFeaturedProperties] = useState(true);
  const [loadingFeaturedCampgrounds, setLoadingFeaturedCampgrounds] = useState(true);

  useEffect(() => {
    fetchNewestListings();
    fetchFeaturedProperties();
    fetchFeaturedCampgrounds();
  }, []);

  const fetchNewestListings = async () => {
    try {
      const response = await fetch('/api/properties?limit=3&sort=newest');
      const data = await response.json();
      console.log('Home page API response:', data);
      if (data.success) {
        console.log('Newest listings received:', data.data);
        setNewestListings(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching newest listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedProperties = async () => {
    try {
      const response = await fetch('/api/properties?limit=3&featured=true');
      const data = await response.json();
      console.log('Featured properties API response:', data);
      if (data.success) {
        console.log('Featured properties received:', data.data);
        setFeaturedProperties(data.data || []);
      } else {
        console.error('Featured properties API error:', data.message);
      }
    } catch (error) {
      console.error('Error fetching featured properties:', error);
    } finally {
      setLoadingFeaturedProperties(false);
    }
  };

  const fetchFeaturedCampgrounds = async () => {
    try {
      const response = await fetch('/api/campgrounds?limit=3&featured=true');
      const data = await response.json();
      console.log('Featured campgrounds API response:', data);
      if (data.success) {
        console.log('Featured campgrounds received:', data.data);
        setFeaturedCampgrounds(data.data || []);
      } else {
        console.error('Featured campgrounds API error:', data.message);
      }
    } catch (error) {
      console.error('Error fetching featured campgrounds:', error);
    } finally {
      setLoadingFeaturedCampgrounds(false);
    }
  };
  const features = [
    {
      icon: <IconSearch size={32} />,
      title: 'Real Estate',
      description: 'Find and explore wilderness real estate properties for sale.',
      color: 'green'
    },
    {
      icon: <IconMapPin size={32} />,
      title: 'Rentals',
      description: 'Discover wilderness rental properties and vacation homes.',
      color: 'blue'
    },
    {
      icon: <IconShare size={32} />,
      title: 'RV / Camping',
      description: 'Find RV parks, camping spots, and outdoor accommodations.',
      color: 'orange'
    }
  ];

  return (
    <div style={{
      backgroundImage: 'url(https://images.pexels.com/photos/417074/pexels-photo-417074.jpeg?auto=compress&cs=tinysrgb&w=1920)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      minHeight: '100vh',
      padding: '20px'
    }}>
      <Container size="xl">
        <Stack align="center" gap="xl" mb="xl">
          <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '30px',
            borderRadius: '15px',
            textAlign: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <Title order={1} size="h1" ta="center" c="white">
              Welcome to WildernessFinder
            </Title>
            <Text size="xl" c="white" ta="center" maw={600} style={{ opacity: 0.9 }}>
              Discover and explore amazing wilderness areas in North America
            </Text>
            <Button size="lg" rightSection={<IconArrowRight size={16} />} color="green" mt="md">
              Start Exploring
            </Button>
          </div>
        </Stack>
      
      <Grid>
        {features.map((feature, index) => {
          const isClickable = feature.title === 'RV / Camping';
          
          return (
            <Grid.Col key={index} span={{ base: 12, md: 4 }}>
              {isClickable ? (
                <Card 
                  component={Link}
                  to="/campgrounds"
                  shadow="lg" 
                  padding="lg" 
                  radius="md" 
                  withBorder 
                  h="100%"
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <Stack align="center" gap="md" ta="center">
                    <Group justify="center" c={feature.color}>
                      {feature.icon}
                    </Group>
                    <Title order={3} c="white">
                      {feature.title}
                    </Title>
                    <Text c="white" style={{ opacity: 0.8 }}>
                      {feature.description}
                    </Text>
                  </Stack>
                </Card>
              ) : (
                <Card 
                  shadow="lg" 
                  padding="lg" 
                  radius="md" 
                  withBorder 
                  h="100%"
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <Stack align="center" gap="md" ta="center">
                    <Group justify="center" c={feature.color}>
                      {feature.icon}
                    </Group>
                    <Title order={3} c="white">
                      {feature.title}
                    </Title>
                    <Text c="white" style={{ opacity: 0.8 }}>
                      {feature.description}
                    </Text>
                  </Stack>
                </Card>
              )}
            </Grid.Col>
          );
        })}
          </Grid>

          {/* Newest Listings Section */}
          <Stack gap="xl" mt="xl">
            <div style={{
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              padding: '20px',
              borderRadius: '10px',
              textAlign: 'center',
              backdropFilter: 'blur(10px)'
            }}>
              <Title order={2} c="white" ta="center">Newest Listings</Title>
              <Text c="white" ta="center" maw={600} mx="auto" style={{ opacity: 0.9 }}>
                Discover the latest wilderness properties added to our platform
              </Text>
            </div>
            
            {loading ? (
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                {[1, 2, 3].map((i) => (
                  <Card key={i} shadow="sm" padding="lg" radius="md" withBorder>
                    <Skeleton height={200} mb="md" />
                    <Skeleton height={20} mb="sm" />
                    <Skeleton height={16} mb="xs" />
                    <Skeleton height={16} width="60%" />
                  </Card>
                ))}
              </SimpleGrid>
            ) : newestListings.length > 0 ? (
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                {newestListings.map((listing) => (
                  <Card key={listing.id} shadow="sm" padding="lg" radius="md" withBorder component={Link} to={`/properties/${listing.id}`} style={{ textDecoration: 'none' }}>
                    <Card.Section>
                      {listing.images && listing.images.length > 0 ? (
                        <Image
                          src={listing.images[0]}
                          height={200}
                          alt={listing.title}
                        />
                      ) : (
                        <div style={{ height: 200, backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <IconHome size={48} color="#6c757d" />
                        </div>
                      )}
                    </Card.Section>

                    <Stack gap="sm" mt="md">
                      <Group justify="space-between">
                        <Badge color="green" variant="light">
                          {listing.propertyType}
                        </Badge>
                        <Badge color="blue" variant="light">
                          {listing.listingType}
                        </Badge>
                      </Group>

                      <Title order={4} c="dark" lineClamp={2}>
                        {listing.title}
                      </Title>

                      <Text c="dimmed" size="sm" lineClamp={2}>
                        {listing.description}
                      </Text>

                      <Group gap="xs" c="dimmed">
                        <IconMapPin size={14} />
                        <Text size="sm">
                          {listing.city}, {listing.state}
                        </Text>
                      </Group>

                      <Group justify="space-between" mt="sm">
                        <Group gap="xs">
                          <IconCurrencyDollar size={16} color="green" />
                          <Text fw={600} c="green" size="lg">
                            ${listing.price?.toLocaleString() || 'Contact for Price'}
                          </Text>
                        </Group>
                        
                        <Group gap="xs" c="dimmed">
                          <IconCalendar size={14} />
                          <Text size="sm">
                            {new Date(listing.createdAt).toLocaleDateString()}
                          </Text>
                        </Group>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            ) : (
              <Card shadow="sm" padding="xl" radius="md" withBorder>
                <Stack align="center" gap="md">
                  <IconHome size={48} color="dimmed" />
                  <Title order={4} c="dimmed">No listings yet</Title>
                  <Text c="dimmed" ta="center">
                    Be the first to add a wilderness property listing!
                  </Text>
                </Stack>
              </Card>
            )}

            {newestListings.length > 0 && (
              <Group justify="center" mt="md">
                <Button component={Link} to="/properties" rightSection={<IconArrowRight size={16} />} color="green">
                  View All Listings
                </Button>
              </Group>
            )}
          </Stack>

          {/* Featured Real Estate Section */}
          <Stack gap="xl" mt="xl">
            <div style={{
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              padding: '20px',
              borderRadius: '10px',
              textAlign: 'center',
              backdropFilter: 'blur(10px)'
            }}>
              <Title order={2} c="white" ta="center">Featured Real Estate</Title>
              <Text c="white" ta="center" maw={600} mx="auto" style={{ opacity: 0.9 }}>
                Explore our handpicked selection of premium wilderness properties
              </Text>
            </div>
            
            {loadingFeaturedProperties ? (
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                {[1, 2, 3].map((i) => (
                  <Card key={i} shadow="sm" padding="lg" radius="md" withBorder>
                    <Skeleton height={200} mb="md" />
                    <Skeleton height={20} mb="sm" />
                    <Skeleton height={16} mb="xs" />
                    <Skeleton height={16} width="60%" />
                  </Card>
                ))}
              </SimpleGrid>
            ) : featuredProperties.length > 0 ? (
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                {featuredProperties.map((listing) => (
                  <Card key={listing.id} shadow="sm" padding="lg" radius="md" withBorder component={Link} to={`/properties/${listing.id}`} style={{ textDecoration: 'none' }}>
                    <Card.Section>
                      {listing.images && listing.images.length > 0 ? (
                        <Image
                          src={listing.images[0]}
                          height={200}
                          alt={listing.title}
                        />
                      ) : (
                        <div style={{ height: 200, backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <IconHome size={48} color="#6c757d" />
                        </div>
                      )}
                    </Card.Section>

                    <Stack gap="sm" mt="md">
                      <Group justify="space-between">
                        <Badge color="green" variant="light">
                          {listing.propertyType}
                        </Badge>
                        <Badge color="blue" variant="light">
                          {listing.listingType}
                        </Badge>
                      </Group>

                      <Title order={4} c="dark" lineClamp={2}>
                        {listing.title}
                      </Title>

                      <Text c="dimmed" size="sm" lineClamp={2}>
                        {listing.description}
                      </Text>

                      <Group gap="xs" c="dimmed">
                        <IconMapPin size={14} />
                        <Text size="sm">
                          {listing.city}, {listing.state}
                        </Text>
                      </Group>

                      <Group justify="space-between" mt="sm">
                        <Group gap="xs">
                          <IconCurrencyDollar size={16} color="green" />
                          <Text fw={600} c="green" size="lg">
                            ${listing.price?.toLocaleString() || 'Contact for Price'}
                          </Text>
                        </Group>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            ) : (
              <Card shadow="sm" padding="xl" radius="md" withBorder>
                <Stack align="center" gap="md">
                  <IconHome size={48} color="dimmed" />
                  <Title order={4} c="dimmed">No featured properties yet</Title>
                  <Text c="dimmed" ta="center">
                    Check back soon for featured real estate listings!
                  </Text>
                </Stack>
              </Card>
            )}

            {featuredProperties.length > 0 && (
              <Group justify="center" mt="md">
                <Button component={Link} to="/properties" rightSection={<IconArrowRight size={16} />} color="green">
                  View All Properties
                </Button>
              </Group>
            )}
          </Stack>

          {/* Featured Campgrounds Section */}
          <Stack gap="xl" mt="xl">
            <div style={{
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              padding: '20px',
              borderRadius: '10px',
              textAlign: 'center',
              backdropFilter: 'blur(10px)'
            }}>
              <Title order={2} c="white" ta="center">Featured Campgrounds</Title>
              <Text c="white" ta="center" maw={600} mx="auto" style={{ opacity: 0.9 }}>
                Discover top-rated RV parks and camping destinations
              </Text>
            </div>
            
            {loadingFeaturedCampgrounds ? (
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                {[1, 2, 3].map((i) => (
                  <Card key={i} shadow="sm" padding="lg" radius="md" withBorder>
                    <Skeleton height={200} mb="md" />
                    <Skeleton height={20} mb="sm" />
                    <Skeleton height={16} mb="xs" />
                    <Skeleton height={16} width="60%" />
                  </Card>
                ))}
              </SimpleGrid>
            ) : featuredCampgrounds.length > 0 ? (
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                {featuredCampgrounds.map((campground) => (
                  <Card 
                    key={campground.id} 
                    shadow="sm" 
                    padding="lg" 
                    radius="md" 
                    withBorder 
                    component={Link}
                    to={`/campgrounds/${campground.id}`}
                    style={{ textDecoration: 'none', cursor: 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <Card.Section>
                      {campground.thumbnail ? (
                        <Image
                          src={campground.thumbnail}
                          height={200}
                          alt={campground.name}
                        />
                      ) : (
                        <div style={{ height: 200, backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <IconTent size={48} color="#6c757d" />
                        </div>
                      )}
                    </Card.Section>

                    <Stack gap="sm" mt="md">
                      <Group justify="space-between">
                        <Badge color="orange" variant="light">
                          {campground.type}
                        </Badge>
                        <Badge color="green" variant="light">
                          Featured
                        </Badge>
                      </Group>

                      <Title order={4} c="dark" lineClamp={2}>
                        {campground.name}
                      </Title>

                      <Text c="dimmed" size="sm" lineClamp={2}>
                        {campground.description}
                      </Text>

                      <Group gap="xs" c="dimmed">
                        <IconMapPin size={14} />
                        <Text size="sm">
                          {campground.city}, {campground.state}
                        </Text>
                      </Group>

                      {campground.mainPhone && (
                        <Group gap="xs" c="dimmed">
                          <IconPhone size={14} />
                          <Text size="sm">
                            {campground.mainPhone}
                          </Text>
                        </Group>
                      )}

                      {campground.amenities && campground.amenities.length > 0 && (
                        <Group gap="xs" mt="xs" wrap="wrap">
                          {campground.amenities.slice(0, 3).map((amenity) => (
                            <Badge key={amenity.id} size="sm" variant="outline" color="gray">
                              {amenity.name}
                            </Badge>
                          ))}
                          {campground.amenities.length > 3 && (
                            <Badge size="sm" variant="outline" color="gray">
                              +{campground.amenities.length - 3} more
                            </Badge>
                          )}
                        </Group>
                      )}
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            ) : (
              <Card shadow="sm" padding="xl" radius="md" withBorder>
                <Stack align="center" gap="md">
                  <IconTent size={48} color="dimmed" />
                  <Title order={4} c="dimmed">No featured campgrounds yet</Title>
                  <Text c="dimmed" ta="center">
                    Check back soon for featured camping destinations!
                  </Text>
                </Stack>
              </Card>
            )}

            {featuredCampgrounds.length > 0 && (
              <Group justify="center" mt="md">
                <Button component={Link} to="/campgrounds" rightSection={<IconArrowRight size={16} />} color="orange">
                  View All Campgrounds
                </Button>
              </Group>
            )}
          </Stack>
        </Container>
      </div>
    );
  };

    export default Home;
